import type { FC } from 'react';

import { ButtonBase, CircularProgress, Tooltip } from '@mui/material';
import { QuestionIcon } from '@truvhq/ui';
import { clsx } from 'clsx';

import { NotificationEvent } from 'features/notifications/lib/constants';
import { useUnreadNotificationCount } from 'features/notifications/lib/NotificationFeedContext';
import { unreadNotificationsLabel } from 'features/notifications/lib/unreadNotificationsLabel';
import { NotificationBellIcon } from 'features/notifications/ui/NotificationBellIcon';
import { useAssistantEnabled } from 'features/onboarding/lib/useAssistantEnabled';
import { useControlCenterNotifications } from 'features/onboarding/lib/useControlCenterNotifications';
import { useChecklistProgress } from 'features/onboarding/lib/useOnboardingChecklist';
import { useOnboardingDialogStore } from 'features/onboarding/lib/useOnboardingDialogStore';
import { trackEvent } from 'shared/lib/analytics/trackEvent';
import { useScreenSize } from 'shared/lib/screen/useScreenSize';

import RocketIcon from './icons/rocket.svg?react';
import { EDGE_SAFE_TOOLTIP } from './tooltipSlotProps';

type Props = {
  /** For a page with no nav to reach the assistant from — see below */
  keepOnMobile?: boolean;
};

const ICON_SIZE = 20;

// Padding is 20px on the pill's outer edges and 14px either side of the divider, so each half sets
// its own sides.
const HALF_CLASS =
  'flex min-w-0 items-center gap-[9px] py-[11px] font-[system-ui] font-medium text-grey100 text-sm leading-5 hover:bg-grey20';

/**
 * Floating launcher for the control center, in the bottom-right corner. Below the panel's z-index so
 * it tucks behind the panel once opened.
 *
 * Gated out here rather than by the caller, as OnboardingDialog is: the checklist hook below fetches
 * without an `enabled` of its own, so a body that bailed on its last line would still have made the
 * request. Both mount at the app root, because a page is no less in need of the assistant for being
 * full-screen — the create-order and template pages use a layout of their own and had no way in.
 *
 * Desktop chrome: on a phone a floating pill lands on top of whatever the page pins to its own
 * bottom edge, so the panel is reached from the nav drawer instead (CL-6904). The verification
 * page is the exception — it has no drawer, and nothing pinned under the form for the pill to cover.
 */
export const AssistantLauncherButton: FC<Props> = ({ keepOnMobile = false }) => {
  const { isMobile } = useScreenSize();

  return useAssistantEnabled() && (keepOnMobile || !isMobile) ? <AssistantLauncher /> : null;
};

const AssistantLauncher: FC = () => {
  const open = useOnboardingDialogStore((state) => state.open);
  const hasNotifications = useControlCenterNotifications();
  // While setup is unfinished the launcher says how far along it is and leads with the rocket; it
  // only offers the assistant in the abstract once there is nothing left to point at. Until the
  // checklist resolves it says neither: on a cold open it used to read "Ask Truv AI" for a beat and
  // then rename itself to the setup step, which looks like the button changed its mind.
  const { progress, isPending } = useChecklistProgress();

  return (
    <div className="fixed right-6 bottom-[max(22px,env(safe-area-inset-bottom))] z-[1200] flex max-w-[calc(100vw-3rem)] overflow-hidden rounded-full border border-grey30 bg-white shadow-[0_10px_25px_rgba(0,0,0,0.12)]">
      <ButtonBase
        // "Setup" is a promise to show the steps, so it opens onto the list rather than the chat.
        onClick={() => open({ tab: 'assistant', expand: Boolean(progress) })}
        // Deliberately still clickable: this is the only way into support, and a slow checklist
        // request is no reason to lock someone out of it.
        aria-busy={isPending}
        className={clsx(HALF_CLASS, hasNotifications ? 'pr-3.5 pl-5' : 'px-5')}
      >
        {isPending ? (
          <CircularProgress size={ICON_SIZE} color="inherit" aria-hidden />
        ) : progress ? (
          <RocketIcon width={ICON_SIZE} height={ICON_SIZE} aria-hidden />
        ) : (
          <QuestionIcon width={ICON_SIZE} height={ICON_SIZE} aria-hidden />
        )}
        <span className="truncate">
          {isPending ? (
            'Loading'
          ) : progress ? (
            <>
              Setup <span className="text-grey60">{`${progress.position} / ${progress.total}`}</span>
            </>
          ) : (
            'Ask Truv AI'
          )}
        </span>
      </ButtonBase>
      {hasNotifications && <NotificationsHalf />}
    </div>
  );
};

const NotificationsHalf: FC = () => {
  const open = useOnboardingDialogStore((state) => state.open);
  const unreadCount = useUnreadNotificationCount();
  const label = unreadNotificationsLabel(unreadCount) ?? 'Notifications';

  const openNotifications = () => {
    trackEvent(NotificationEvent.BELL_CLICK, { unread_count: unreadCount });
    open({ tab: 'notifications', expand: true });
  };

  return (
    <Tooltip title={label} placement="top" slotProps={EDGE_SAFE_TOOLTIP}>
      <ButtonBase
        aria-label={label}
        onClick={openNotifications}
        // border-solid because ButtonBase's `border: 0` also resets the style, which a width class
        // leaves alone. The dot's ring follows the fill, so it stays a cut-out of the bell on hover too.
        className={clsx(
          HALF_CLASS,
          'border-grey30 border-l-[1px] border-solid pr-5 pl-3.5',
          '[--bell-ring-color:theme(colors.white)] hover:[--bell-ring-color:theme(colors.grey20)]',
        )}
      >
        <NotificationBellIcon hasUnread={unreadCount > 0} />
      </ButtonBase>
    </Tooltip>
  );
};
